import { Check } from 'lucide-react';

import { SOCIAL_PLATFORMS, type SocialPlatform, type VenueSocial } from '../../../../api/venues';

import { FormAlert } from './FormAlert';
import { SOCIAL_COLUMNS, SocialPlatformRow } from './SocialPlatformRow';
import { SocialWebsite } from './SocialWebsite';

export interface SocialNotice {
  ok: boolean;
  text: string;
}

interface SocialPresenceViewProps {
  social: VenueSocial | null;
  compact: boolean;
  /** Present only for someone who may edit marketing. */
  onVerify?: (platform: SocialPlatform) => void;
  busy: boolean;
  notice: SocialNotice | null;
}

const headStyle = {
  display: 'grid',
  gridTemplateColumns: SOCIAL_COLUMNS,
  gap: 10,
  paddingBottom: 6,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: 'var(--nb-subtle)',
} as const;

/** Read view of the social presence: a row per platform, then the website. */
export function SocialPresenceView({
  social,
  compact,
  onVerify,
  busy,
  notice,
}: SocialPresenceViewProps) {
  return (
    <div>
      {!compact && (
        <div style={headStyle}>
          <span>Platform</span>
          <span>Handle</span>
          <span style={{ textAlign: 'right' }}>Audience</span>
          <span>Cadence</span>
          <span>Last verified</span>
        </div>
      )}
      {SOCIAL_PLATFORMS.map((platform) => (
        <SocialPlatformRow
          key={platform}
          platform={platform}
          presence={social?.[platform]}
          compact={compact}
          onVerify={onVerify}
          busy={busy}
        />
      ))}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: compact ? '1fr' : '80px 1fr',
          gap: 10,
          alignItems: 'center',
          padding: '10px 0 0',
          borderTop: '1px solid var(--nb-border)',
          fontSize: 12.5,
        }}
      >
        <div style={{ fontWeight: 700, color: 'var(--nb-ink)' }}>Website</div>
        <div style={{ minWidth: 0 }}>
          <SocialWebsite url={social?.website} />
        </div>
      </div>
      {notice?.ok && (
        <div
          role="status"
          style={{
            marginTop: 10,
            fontSize: 11,
            color: 'var(--nb-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Check size={12} aria-hidden="true" />
          {notice.text}
        </div>
      )}
      {notice && !notice.ok && (
        <div style={{ marginTop: 10 }}>
          <FormAlert message={notice.text} />
        </div>
      )}
    </div>
  );
}
